// src/bytes.ts
var SECP256K1_P = 0xfffffffffffffffffffffffffffffffffffffffffffffffffffffffefffffc2fn;
var SECP256K1_N = 0xfffffffffffffffffffffffffffffffebaaedce6af48a03bbfd25e8cd0364141n;
var SECP256K1_GX = 55066263022277343669578718895168534326250603453777594175500187360389116729240n;
var SECP256K1_GY = 32670510020758816978083085130507043184471273380659243275938904335757337482424n;
var MASK_64 = (1n << 64n) - 1n;
function concatBytes(...arrays) {
  const totalLength = arrays.reduce((sum, array) => sum + array.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const array of arrays) {
    result.set(array, offset);
    offset += array.length;
  }
  return result;
}
function utf8ToBytes(value) {
  return new TextEncoder().encode(value);
}
function hexToBytes(hex) {
  const normalized = hex.startsWith("0x") || hex.startsWith("0X") ? hex.slice(2) : hex;
  const padded = normalized.length % 2 === 0 ? normalized : `0${normalized}`;
  const output = new Uint8Array(padded.length / 2);
  for (let index = 0; index < padded.length; index += 2) {
    output[index / 2] = Number.parseInt(padded.slice(index, index + 2), 16);
  }
  return output;
}
function bytesToHex(bytes) {
  return `0x${Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}
function bytesToBigInt(bytes) {
  let value = 0n;
  for (const byte of bytes) {
    value = value << 8n | BigInt(byte);
  }
  return value;
}
function leftPad32(bytes) {
  if (bytes.length > 32) {
    throw new Error("leftPad32: input exceeds 32 bytes.");
  }
  const output = new Uint8Array(32);
  output.set(bytes, 32 - bytes.length);
  return output;
}
function bigintTo32Bytes(value) {
  if (value < 0n) {
    throw new Error("bigintTo32Bytes: negative values are not supported.");
  }
  const hex = value.toString(16).padStart(64, "0");
  if (hex.length > 64) {
    throw new Error("bigintTo32Bytes: value exceeds 32 bytes.");
  }
  return hexToBytes(hex);
}
function mod(value, modulus) {
  const result = value % modulus;
  return result >= 0n ? result : result + modulus;
}
function invert(value, modulus) {
  let a = mod(value, modulus);
  let b = modulus;
  let x = 1n;
  let y = 0n;
  while (b !== 0n) {
    const quotient = a / b;
    [a, b] = [b, a % b];
    [x, y] = [y, x - quotient * y];
  }
  if (a !== 1n) {
    throw new Error("Value is not invertible.");
  }
  return mod(x, modulus);
}
function modPow(base, exponent, modulus) {
  let result = 1n;
  let factor = mod(base, modulus);
  let power = exponent;
  while (power > 0n) {
    if ((power & 1n) === 1n) {
      result = mod(result * factor, modulus);
    }
    factor = mod(factor * factor, modulus);
    power >>= 1n;
  }
  return result;
}

// src/keccak.ts
var KECCAK_RATE_BYTES = 136;
var KECCAK_ROTATION_OFFSETS = [
  0n,
  1n,
  62n,
  28n,
  27n,
  36n,
  44n,
  6n,
  55n,
  20n,
  3n,
  10n,
  43n,
  25n,
  39n,
  41n,
  45n,
  15n,
  21n,
  8n,
  18n,
  2n,
  61n,
  56n,
  14n
];
var KECCAK_ROUND_CONSTANTS = [
  0x0000000000000001n,
  0x0000000000008082n,
  0x800000000000808an,
  0x8000000080008000n,
  0x000000000000808bn,
  0x0000000080000001n,
  0x8000000080008081n,
  0x8000000000008009n,
  0x000000000000008an,
  0x0000000000000088n,
  0x0000000080008009n,
  0x000000008000000an,
  0x000000008000808bn,
  0x800000000000008bn,
  0x8000000000008089n,
  0x8000000000008003n,
  0x8000000000008002n,
  0x8000000000000080n,
  0x000000000000800an,
  0x800000008000000an,
  0x8000000080008081n,
  0x8000000000008080n,
  0x0000000080000001n,
  0x8000000080008008n
];
function keccak256(input) {
  const state = Array.from({ length: 25 }, () => 0n);
  let offset = 0;
  while (offset + KECCAK_RATE_BYTES <= input.length) {
    absorbChunk(state, input.subarray(offset, offset + KECCAK_RATE_BYTES));
    keccakF1600(state);
    offset += KECCAK_RATE_BYTES;
  }
  const block = new Uint8Array(KECCAK_RATE_BYTES);
  block.set(input.subarray(offset));
  const suffixIndex = input.length - offset;
  block[suffixIndex] = (block[suffixIndex] ?? 0) ^ 1;
  const finalIndex = KECCAK_RATE_BYTES - 1;
  block[finalIndex] = (block[finalIndex] ?? 0) ^ 128;
  absorbChunk(state, block);
  keccakF1600(state);
  const output = new Uint8Array(32);
  for (let index = 0; index < output.length; index += 1) {
    const lane = state[Math.floor(index / 8)] ?? 0n;
    output[index] = Number(lane >> 8n * BigInt(index % 8) & 0xffn);
  }
  return output;
}
function absorbChunk(state, block) {
  for (let lane = 0; lane < KECCAK_RATE_BYTES / 8; lane += 1) {
    let value = 0n;
    for (let offset = 0; offset < 8; offset += 1) {
      value |= BigInt(block[lane * 8 + offset] ?? 0) << 8n * BigInt(offset);
    }
    state[lane] = (state[lane] ?? 0n) ^ value;
  }
}
function keccakF1600(state) {
  for (const roundConstant of KECCAK_ROUND_CONSTANTS) {
    const c = new Array(5).fill(0n);
    for (let x = 0; x < 5; x += 1) {
      c[x] = (state[x] ?? 0n) ^ (state[x + 5] ?? 0n) ^ (state[x + 10] ?? 0n) ^ (state[x + 15] ?? 0n) ^ (state[x + 20] ?? 0n);
    }
    const d = new Array(5).fill(0n);
    for (let x = 0; x < 5; x += 1) {
      d[x] = (c[(x + 4) % 5] ?? 0n) ^ rotateLeft64(c[(x + 1) % 5] ?? 0n, 1n);
    }
    for (let x = 0; x < 5; x += 1) {
      for (let y = 0; y < 5; y += 1) {
        state[x + 5 * y] = mod64((state[x + 5 * y] ?? 0n) ^ (d[x] ?? 0n));
      }
    }
    const b = new Array(25).fill(0n);
    for (let x = 0; x < 5; x += 1) {
      for (let y = 0; y < 5; y += 1) {
        const index = x + 5 * y;
        const destination = y + 5 * ((2 * x + 3 * y) % 5);
        b[destination] = rotateLeft64(state[index] ?? 0n, KECCAK_ROTATION_OFFSETS[index] ?? 0n);
      }
    }
    for (let x = 0; x < 5; x += 1) {
      for (let y = 0; y < 5; y += 1) {
        const index = x + 5 * y;
        state[index] = mod64(
          (b[index] ?? 0n) ^ ~(b[(x + 1) % 5 + 5 * y] ?? 0n) & (b[(x + 2) % 5 + 5 * y] ?? 0n)
        );
      }
    }
    state[0] = mod64((state[0] ?? 0n) ^ roundConstant);
  }
}
function rotateLeft64(value, shift) {
  if (shift === 0n) {
    return mod64(value);
  }
  return mod64(value << shift | value >> 64n - shift);
}
function mod64(value) {
  return value & MASK_64;
}

// src/secp256k1.ts
var Secp256k1Point = class _Secp256k1Point {
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
  static BASE = new _Secp256k1Point(SECP256K1_GX, SECP256K1_GY);
  static ZERO = new _Secp256k1Point(null, null);
  get isInfinity() {
    return this.x === null || this.y === null;
  }
  negate() {
    if (this.isInfinity || this.x === null || this.y === null) {
      return this;
    }
    return new _Secp256k1Point(this.x, mod(-this.y, SECP256K1_P));
  }
  add(other) {
    if (this.isInfinity) {
      return other;
    }
    if (other.isInfinity) {
      return this;
    }
    if (this.x === null || this.y === null || other.x === null || other.y === null) {
      return _Secp256k1Point.ZERO;
    }
    if (this.x === other.x) {
      if (mod(this.y + other.y, SECP256K1_P) === 0n) {
        return _Secp256k1Point.ZERO;
      }
      return this.double();
    }
    const slope = mod(
      (other.y - this.y) * invert(mod(other.x - this.x, SECP256K1_P), SECP256K1_P),
      SECP256K1_P
    );
    const x = mod(slope * slope - this.x - other.x, SECP256K1_P);
    const y = mod(slope * (this.x - x) - this.y, SECP256K1_P);
    return new _Secp256k1Point(x, y);
  }
  double() {
    if (this.isInfinity || this.x === null || this.y === null || this.y === 0n) {
      return _Secp256k1Point.ZERO;
    }
    const slope = mod(3n * this.x * this.x * invert(2n * this.y, SECP256K1_P), SECP256K1_P);
    const x = mod(slope * slope - 2n * this.x, SECP256K1_P);
    const y = mod(slope * (this.x - x) - this.y, SECP256K1_P);
    return new _Secp256k1Point(x, y);
  }
  /** Scalar multiplication via straight double-and-add. */
  multiply(scalar) {
    let result = _Secp256k1Point.ZERO;
    let addend = new _Secp256k1Point(this.x, this.y);
    let value = scalar;
    while (value > 0n) {
      if ((value & 1n) === 1n) {
        result = result.add(addend);
      }
      addend = addend.double();
      value >>= 1n;
    }
    return result;
  }
};
function recoverEthereumAddress(digest, signature) {
  if (digest.length !== 32 || signature.length !== 65) {
    return void 0;
  }
  const recoveryByte = signature[64];
  if (recoveryByte === void 0) {
    return void 0;
  }
  const recovery = normalizeRecoveryId(recoveryByte);
  if (recovery === void 0) {
    return void 0;
  }
  const r = bytesToBigInt(signature.subarray(0, 32));
  const s = bytesToBigInt(signature.subarray(32, 64));
  if (r <= 0n || r >= SECP256K1_N || s <= 0n || s >= SECP256K1_N) {
    return void 0;
  }
  const recoveredPoint = recoverPublicKey(digest, r, s, recovery);
  if (!recoveredPoint || recoveredPoint.isInfinity || recoveredPoint.x === null || recoveredPoint.y === null) {
    return void 0;
  }
  const publicKeyBytes = concatBytes(
    Uint8Array.from([4]),
    bigintTo32Bytes(recoveredPoint.x),
    bigintTo32Bytes(recoveredPoint.y)
  );
  return deriveEthereumAddressFromPublicKey(publicKeyBytes);
}
function normalizeRecoveryId(recoveryByte) {
  if (recoveryByte === 27 || recoveryByte === 28) {
    return recoveryByte - 27;
  }
  if (recoveryByte === 0 || recoveryByte === 1) {
    return recoveryByte;
  }
  return void 0;
}
function deriveEthereumAddressFromPublicKey(publicKey) {
  let uncompressed;
  if (publicKey.length === 65 && publicKey[0] === 4) {
    uncompressed = publicKey.subarray(1);
  } else if (publicKey.length === 64) {
    uncompressed = publicKey;
  } else {
    throw new Error(
      `deriveEthereumAddressFromPublicKey: expected 65-byte (0x04||X||Y) or 64-byte (X||Y) public key, got ${publicKey.length} bytes` + (publicKey.length === 33 ? " (looks like a compressed key \u2014 decode with parseSecp256k1PublicKey and re-encode first)" : "")
    );
  }
  const hashed = keccak256(uncompressed);
  return bytesToHex(hashed.subarray(12)).toLowerCase();
}
function recoverPublicKey(digestBytes, r, s, recovery) {
  if (digestBytes.length !== 32) {
    return void 0;
  }
  if (r <= 0n || r >= SECP256K1_N || s <= 0n || s >= SECP256K1_N) {
    return void 0;
  }
  if (recovery !== 0 && recovery !== 1) {
    return void 0;
  }
  const xCandidates = [r];
  const overflowCandidate = r + SECP256K1_N;
  if (overflowCandidate < SECP256K1_P) {
    xCandidates.push(overflowCandidate);
  }
  const z = mod(bytesToBigInt(digestBytes), SECP256K1_N);
  const rInverse = invert(r, SECP256K1_N);
  for (const x of xCandidates) {
    const ySquared = mod(modPow(x, 3n, SECP256K1_P) + 7n, SECP256K1_P);
    const yRoot = modPow(ySquared, SECP256K1_P + 1n >> 2n, SECP256K1_P);
    if (mod(yRoot * yRoot, SECP256K1_P) !== ySquared) {
      continue;
    }
    const y = Number(yRoot & 1n) === recovery ? yRoot : mod(-yRoot, SECP256K1_P);
    const point = new Secp256k1Point(x, y);
    if (!point.multiply(SECP256K1_N).isInfinity) {
      continue;
    }
    const recovered = point.multiply(s).add(Secp256k1Point.BASE.multiply(z).negate()).multiply(rInverse);
    if (isValidRecoveredPublicKey(digestBytes, r, s, recovered)) {
      return recovered;
    }
  }
  return void 0;
}
function isValidRecoveredPublicKey(digestBytes, r, s, publicKey) {
  if (publicKey.isInfinity) {
    return false;
  }
  const z = mod(bytesToBigInt(digestBytes), SECP256K1_N);
  const sInverse = invert(s, SECP256K1_N);
  const u1 = mod(z * sInverse, SECP256K1_N);
  const u2 = mod(r * sInverse, SECP256K1_N);
  const check = Secp256k1Point.BASE.multiply(u1).add(publicKey.multiply(u2));
  return !check.isInfinity && check.x !== null && mod(check.x, SECP256K1_N) === r;
}

// src/eip712.ts
var HEX_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
var HEX_BYTES32 = /^0x[0-9a-fA-F]{64}$/;
var DECIMAL_INTEGER = /^(0|[1-9][0-9]*)$/;
function typeHash(typeName, fields) {
  return keccak256(utf8ToBytes(encodeType(typeName, fields)));
}
function encodeType(typeName, fields) {
  return `${typeName}(${fields.map((field) => `${field.type} ${field.name}`).join(",")})`;
}
function hashStruct(typeName, fields, value) {
  const encodedTypeHash = typeHash(typeName, fields);
  const encodedFields = fields.map((field) => encodeFieldValue(field.type, value[field.name]));
  return keccak256(concatBytes(encodedTypeHash, ...encodedFields));
}
function eip712Digest(domainSeparator, messageHash) {
  if (domainSeparator.length !== 32 || messageHash.length !== 32) {
    throw new Error("eip712Digest: expected 32-byte domainSeparator and messageHash.");
  }
  return keccak256(concatBytes(Uint8Array.from([25, 1]), domainSeparator, messageHash));
}
function encodeFieldValue(type, value) {
  switch (type) {
    case "address": {
      if (typeof value !== "string" || !HEX_ADDRESS.test(value)) {
        throw new Error(`EIP-712: expected a 20-byte hex address, got ${describe(value)}.`);
      }
      return leftPad32(hexToBytes(value));
    }
    case "string": {
      if (typeof value !== "string") {
        throw new Error(`EIP-712: expected a string, got ${describe(value)}.`);
      }
      return keccak256(utf8ToBytes(value));
    }
    case "bytes32": {
      if (typeof value !== "string" || !HEX_BYTES32.test(value)) {
        throw new Error(`EIP-712: expected a 32-byte hex string, got ${describe(value)}.`);
      }
      return hexToBytes(value);
    }
    case "uint64":
    case "uint256": {
      const numeric = toNonNegativeBigint(value);
      if (numeric === void 0) {
        throw new Error(`EIP-712: expected a non-negative integer ${type}, got ${describe(value)}.`);
      }
      if (type === "uint64" && numeric > 0xffffffffffffffffn) {
        throw new Error(`EIP-712: uint64 overflow: ${numeric.toString()}`);
      }
      return bigintTo32Bytes(numeric);
    }
  }
}
function toNonNegativeBigint(value) {
  if (typeof value === "bigint") {
    return value >= 0n ? value : void 0;
  }
  if (typeof value === "number") {
    if (!Number.isInteger(value) || value < 0) {
      return void 0;
    }
    return BigInt(value);
  }
  if (typeof value === "string" && DECIMAL_INTEGER.test(value)) {
    return BigInt(value);
  }
  return void 0;
}
function describe(value) {
  if (typeof value === "string") {
    return `"${value}"`;
  }
  if (typeof value === "bigint") {
    return `${value.toString()}n`;
  }
  if (value === null || value === void 0) {
    return String(value);
  }
  return typeof value;
}

// src/eip191.ts
var EIP191_PREFIX = utf8ToBytes("Ethereum Signed Message:\n");
function eip191Digest(message) {
  const lengthPrefix = utf8ToBytes(String(message.length));
  return keccak256(concatBytes(EIP191_PREFIX, lengthPrefix, message));
}

// src/ecdsa.ts
function parseSecp256k1PublicKey(bytes) {
  if (bytes.length === 65 && bytes[0] === 4) {
    const x = bytesToBigInt(bytes.subarray(1, 33));
    const y = bytesToBigInt(bytes.subarray(33, 65));
    return isOnCurve(x, y) ? new Secp256k1Point(x, y) : void 0;
  }
  if (bytes.length === 64) {
    const x = bytesToBigInt(bytes.subarray(0, 32));
    const y = bytesToBigInt(bytes.subarray(32, 64));
    return isOnCurve(x, y) ? new Secp256k1Point(x, y) : void 0;
  }
  if (bytes.length === 33 && (bytes[0] === 2 || bytes[0] === 3)) {
    const x = bytesToBigInt(bytes.subarray(1, 33));
    if (x <= 0n || x >= SECP256K1_P) {
      return void 0;
    }
    const ySquared = mod(modPow(x, 3n, SECP256K1_P) + 7n, SECP256K1_P);
    const yRoot = modPow(ySquared, SECP256K1_P + 1n >> 2n, SECP256K1_P);
    if (mod(yRoot * yRoot, SECP256K1_P) !== ySquared) {
      return void 0;
    }
    const wantOdd = bytes[0] === 3;
    const y = (yRoot & 1n) === (wantOdd ? 1n : 0n) ? yRoot : mod(-yRoot, SECP256K1_P);
    return new Secp256k1Point(x, y);
  }
  return void 0;
}
function verifyEcdsaSecp256k1(digest, signature, publicKey) {
  if (digest.length !== 32) {
    return false;
  }
  const parsed = parseEcdsaSignature(signature);
  if (!parsed) {
    return false;
  }
  const { r, s } = parsed;
  if (r <= 0n || r >= SECP256K1_N || s <= 0n || s >= SECP256K1_N) {
    return false;
  }
  const point = parseSecp256k1PublicKey(publicKey);
  if (!point || point.isInfinity) {
    return false;
  }
  const z = mod(bytesToBigInt(digest), SECP256K1_N);
  let sInverse;
  try {
    sInverse = invert(s, SECP256K1_N);
  } catch {
    return false;
  }
  const u1 = mod(z * sInverse, SECP256K1_N);
  const u2 = mod(r * sInverse, SECP256K1_N);
  const check = Secp256k1Point.BASE.multiply(u1).add(point.multiply(u2));
  return !check.isInfinity && check.x !== null && mod(check.x, SECP256K1_N) === r;
}
function parseEcdsaSignature(signature) {
  if (signature.length === 64) {
    return {
      r: bytesToBigInt(signature.subarray(0, 32)),
      s: bytesToBigInt(signature.subarray(32, 64))
    };
  }
  if (signature.length >= 8 && signature[0] === 48) {
    return parseDerEcdsaSignature(signature);
  }
  return void 0;
}
function parseDerEcdsaSignature(signature) {
  let offset = 0;
  if (signature[offset++] !== 48) {
    return void 0;
  }
  const totalLen = signature[offset++];
  if (totalLen === void 0 || totalLen >= 128 || totalLen !== signature.length - 2) {
    return void 0;
  }
  if (signature[offset++] !== 2) {
    return void 0;
  }
  const rLen = signature[offset++];
  if (rLen === void 0 || rLen === 0 || rLen >= 128 || offset + rLen > signature.length) {
    return void 0;
  }
  const rBytes = signature.subarray(offset, offset + rLen);
  offset += rLen;
  if (signature[offset++] !== 2) {
    return void 0;
  }
  const sLen = signature[offset++];
  if (sLen === void 0 || sLen === 0 || sLen >= 128 || offset + sLen !== signature.length) {
    return void 0;
  }
  const sBytes = signature.subarray(offset, offset + sLen);
  if (rBytes.length > 1 && rBytes[0] === 0 && ((rBytes[1] ?? 0) & 128) === 0) {
    return void 0;
  }
  if (sBytes.length > 1 && sBytes[0] === 0 && ((sBytes[1] ?? 0) & 128) === 0) {
    return void 0;
  }
  return {
    r: bytesToBigInt(rBytes),
    s: bytesToBigInt(sBytes)
  };
}
function isOnCurve(x, y) {
  if (x <= 0n || x >= SECP256K1_P || y <= 0n || y >= SECP256K1_P) {
    return false;
  }
  const lhs = mod(y * y, SECP256K1_P);
  const rhs = mod(modPow(x, 3n, SECP256K1_P) + 7n, SECP256K1_P);
  return lhs === rhs;
}
export {
  SECP256K1_GX,
  SECP256K1_GY,
  SECP256K1_N,
  SECP256K1_P,
  Secp256k1Point,
  bigintTo32Bytes,
  bytesToBigInt,
  bytesToHex,
  concatBytes,
  deriveEthereumAddressFromPublicKey,
  eip191Digest,
  eip712Digest,
  encodeFieldValue,
  encodeType,
  hashStruct,
  hexToBytes,
  invert,
  keccak256,
  leftPad32,
  mod,
  modPow,
  normalizeRecoveryId,
  parseEcdsaSignature,
  parseSecp256k1PublicKey,
  recoverEthereumAddress,
  recoverPublicKey,
  typeHash,
  utf8ToBytes,
  verifyEcdsaSecp256k1
};
